/**
 * `/poste` — the scan station. The desk's resting screen.
 *
 * A hardware scanner presents itself as a keyboard, so there is no camera,
 * no `getUserMedia`, and therefore no secure-context requirement: this works
 * over plain HTTP on a machine that will never have a certificate. The payload
 * the scanner types is the same pass URL the phone's camera decodes, so both
 * clients go down the identical `normalizeScanInput` → `deriveScanVerdict`
 * path on the server. No second verdict implementation.
 *
 * **The entry is recorded by the scan, not by a confirmation.** At a desk, a
 * code presented to a scanner is an unambiguous physical act by a person who
 * is standing there — unlike a camera, which can catch a QR across a lobby and
 * therefore has to ask. Making the gardien confirm every arrival is the
 * friction that gets a tool abandoned by the second week. The one exception is
 * a pass valid for a party: there the number of people who actually turned up
 * is a fact only the gardien can supply, so the verdict waits for it.
 *
 * The second exception is a visitor the syndic put on the liste noire who is
 * ALSO holding a pass that scans `allow`. Blocking someone does not
 * retroactively invalidate a pass a résident issued, so `validate` returns the
 * allow with `blocked` alongside it. Recording that entry automatically would
 * log an admission the gardien never chose, so the station writes nothing and
 * asks: refuse, or admit anyway. Either answer is what lands in the log.
 *
 * A blocked visitor whose pass ALSO fails (expired, revoked, off-schedule)
 * gets no such choice: the deny is recorded the moment it is read, under its
 * own reason, exactly as any other deny — the liste noire is then just a note
 * on a screen. Offering « Admettre malgré tout » there would let one click
 * write an allow against, and burn, a pass the gate had already refused.
 */
import type { RouterOutputs } from "@gately/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Keyboard, ScanLine, TriangleAlert, X } from "lucide-react";

import { Button } from "@gately/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import { Input } from "@gately/ui/input";
import { toast } from "@gately/ui/toast";

import type { StationLayout } from "~/lib/station/keyboard-layout";
import { StationChrome } from "~/components/station/station-chrome";
import { StationEyebrow } from "~/components/station/station-screen";
import { VisitorBadges } from "~/components/visitors/visitor-badges";
import { orpc, orpcClient } from "~/lib/orpc";
import { playVerdictTone } from "~/lib/station/beep";
import { looksLayoutMangled, salvageScanPayload } from "~/lib/station/hid";
import { LAYOUT_LABEL, repairLayout } from "~/lib/station/keyboard-layout";
import {
  denyReasonDetail,
  denyReasonLabel,
  validityLabel,
} from "~/lib/station/scan-format";
import { useHidScanner } from "~/lib/station/use-hid-scanner";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/poste/")({
  loader: async ({ context }) => {
    // The chrome reads `shift.current` with `useSuspenseQuery`; the layout's
    // gate already fetched it, this keeps the contract explicit.
    await context.queryClient.ensureQueryData(
      orpc.shift.current.queryOptions(),
    );
  },
  component: ScanStationPage,
});

type Verdict = RouterOutputs["scan"]["validate"];

/** How long a verdict stays up before the desk returns to ready. */
const ALLOW_HOLD_MS = 3_500;
const DENY_HOLD_MS = 6_000;

/**
 * What the desk is doing. `headcount` is the one state that waits for a human:
 * everything else either resolves itself or times out.
 */
type Screen =
  | { kind: "ready" }
  | { kind: "checking" }
  | { kind: "headcount"; verdict: Verdict; raw: string }
  | { kind: "verdict"; verdict: Verdict; raw: string; recorded: boolean };

function ScanStationPage() {
  const queryClient = useQueryClient();
  const terms = useTerms();
  const [screen, setScreen] = useState<Screen>({ kind: "ready" });
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [hint, setHint] = useState<string | null>(null);
  // Latches once a scan had to be repaired. The reading worked, but the
  // station is misconfigured and the next payload may not be recoverable, so
  // this stays on screen rather than flashing once.
  // Names the layout when we could identify it, so the warning can say what to
  // change instead of just that something is wrong. `true` = mangled but
  // unidentified.
  const [layoutSuspect, setLayoutSuspect] = useState<
    StationLayout | true | null
  >(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearResetTimer = () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = null;
  };

  const reset = useCallback(() => {
    clearResetTimer();
    setScreen({ kind: "ready" });
  }, []);

  useEffect(() => clearResetTimer, []);

  const invalidateLog = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.scan.today.key() });
  }, [queryClient]);

  /**
   * The gardien's answer to a blocked visitor, written to the log and then
   * off the screen. This is the only path where an entry_event is created by
   * a click rather than by the scan itself — see the header.
   */
  const recordBlockedDecision = useCallback(
    async (verdict: Verdict, result: "allow" | "deny", reason?: string) => {
      const invite = verdict.invite;
      if (!invite) return;
      try {
        await orpcClient.scan.record({
          source: "scan",
          inviteId: invite.id,
          guestId: invite.guestId,
          result,
          reason,
        });
      } catch (e) {
        toast.error(
          (e as { message?: string }).message ??
            "Le passage n'a pas pu être enregistré.",
        );
        return;
      }
      await invalidateLog();
      toast.success(
        result === "allow" ? "Entrée enregistrée." : "Refus enregistré.",
      );
      reset();
    },
    [invalidateLog, reset],
  );

  /**
   * Record and show. Recording failures are surfaced but do not hide the
   * verdict: the gardien still has to decide about the person in front of
   * them, and "the log write failed" is not their problem to solve at the
   * door.
   */
  const settle = useCallback(
    async (verdict: Verdict, raw: string) => {
      playVerdictTone(verdict.result);
      let recorded = false;
      try {
        if (verdict.kind === "resident" && verdict.resident) {
          await orpcClient.scan.record({
            source: "resident",
            userId: verdict.resident.userId,
            unitId: verdict.resident.unitId,
            // A resident verdict that resolves a live membership is always an
            // allow, so there is no reason code to carry.
            result: verdict.result,
          });
          recorded = true;
        } else if (
          verdict.invite &&
          !(verdict.blocked && verdict.result === "allow")
        ) {
          await orpcClient.scan.record({
            source: "scan",
            // `validate` returns the GUEST id here, which `record` resolves as
            // a guest first — so a group invite's one-time burn hits the right
            // person. See the comments on both handlers.
            inviteId: verdict.invite.id,
            guestId: verdict.invite.guestId,
            result: verdict.result,
            reason: verdict.reason ?? undefined,
          });
          recorded = true;
        }
        // A `not_found` deny has nothing to reference, so there is nothing to
        // write. Inventing an entry_event for a code that matches no pass
        // would put a fiction in the log.
      } catch (e) {
        toast.error(
          (e as { message?: string }).message ??
            "Le passage n'a pas pu être enregistré.",
        );
      }
      if (recorded) await invalidateLog();

      setScreen({ kind: "verdict", verdict, raw, recorded });
      clearResetTimer();
      // Only the screen that is WAITING on the gardien holds: it carries two
      // buttons and nothing has been written yet, so a timeout would drop the
      // decision on the floor. A blocked deny recorded itself and behaves like
      // any other deny. (`blocked` is only ever set on the guest branch, which
      // always has an invite — so this needs no second test.)
      if (!(verdict.blocked && verdict.result === "allow")) {
        resetTimer.current = setTimeout(
          reset,
          verdict.result === "allow" ? ALLOW_HOLD_MS : DENY_HOLD_MS,
        );
      }
    },
    [invalidateLog, reset],
  );

  const check = useCallback(
    (payload: { token?: string; code?: string }, raw: string) => {
      clearResetTimer();
      setHint(null);
      setScreen({ kind: "checking" });
      void (async () => {
        try {
          const verdict = await orpcClient.scan.validate(payload);
          // A party pass is the only case where the desk stops and asks: the
          // headcount is a fact the gardien holds and nobody else does.
          // A blocked visitor never reaches the headcount step: that step
          // ends in an automatic record, which is exactly what must not
          // happen before the gardien has answered.
          const max =
            verdict.result === "allow" &&
            verdict.kind === "guest" &&
            !verdict.blocked
              ? verdict.invite.maxHeadcount
              : null;
          if (max !== null && max > 1) {
            playVerdictTone("allow");
            setScreen({ kind: "headcount", verdict, raw });
            return;
          }
          await settle(verdict, raw);
        } catch (e) {
          setScreen({ kind: "ready" });
          toast.error(
            (e as { message?: string }).message ?? "Vérification impossible.",
          );
        }
      })();
    },
    [settle],
  );

  // The scanner. Stood down while a verdict or the headcount step is up, so a
  // scan arriving mid-verdict is not swallowed by the outgoing one — it simply
  // has to be presented again, which is what the gardien would do anyway.
  useHidScanner({
    enabled: screen.kind === "ready" && !manualOpen,
    onScan: (payload) => {
      // A mismatched keyboard layout mangles the punctuation but leaves the
      // token intact. Send what we can actually read rather than refusing a
      // guest over a colon. `raw` stays the unrepaired reading, so the verdict
      // can still say the reader needs configuring.
      const salvaged = salvageScanPayload(payload);
      if (salvaged) setLayoutSuspect(repairLayout(payload)?.layout ?? true);
      check({ token: salvaged ?? payload }, payload);
    },
    onDrop: (reason) => {
      if (reason === "too_short") {
        setHint("Lecture incomplète. Représentez le code.");
      }
    },
  });

  const submitManual = () => {
    const code = manualCode.trim();
    if (!code) return;
    setManualOpen(false);
    setManualCode("");
    check({ code }, code);
  };

  return (
    <StationChrome bleed>
      {screen.kind === "verdict" ? (
        <VerdictScreen
          verdict={screen.verdict}
          raw={screen.raw}
          recorded={screen.recorded}
          onDismiss={reset}
          onBlockedDecision={(result, reason) =>
            recordBlockedDecision(screen.verdict, result, reason)
          }
        />
      ) : screen.kind === "headcount" ? (
        <HeadcountScreen
          verdict={screen.verdict}
          onConfirm={(actualHeadcount) => {
            void (async () => {
              const invite = screen.verdict.invite;
              if (!invite) return;
              try {
                await orpcClient.scan.record({
                  source: "scan",
                  inviteId: invite.id,
                  guestId: invite.guestId,
                  result: "allow",
                  actualHeadcount,
                });
                await invalidateLog();
                setScreen({
                  kind: "verdict",
                  verdict: screen.verdict,
                  raw: screen.raw,
                  recorded: true,
                });
                clearResetTimer();
                resetTimer.current = setTimeout(reset, ALLOW_HOLD_MS);
              } catch (e) {
                toast.error(
                  (e as { message?: string }).message ??
                    "Le passage n'a pas pu être enregistré.",
                );
              }
            })();
          }}
          onCancel={reset}
        />
      ) : (
        <ReadyScreen
          checking={screen.kind === "checking"}
          hint={hint}
          layoutSuspect={layoutSuspect}
          onManual={() => setManualOpen(true)}
        />
      )}

      <Dialog
        open={manualOpen}
        onOpenChange={(open) => {
          setManualOpen(open);
          if (!open) setManualCode("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>QR illisible</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            Entrez le code imprimé sur l&apos;invitation — par exemple
            PASS-4K12-8B, ou RES-B12-4F9A pour un{" "}
            {terms.residentLabel.toLowerCase()}.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitManual();
            }}
          >
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              placeholder="PASS-XXXX-XX"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              className="h-14 font-mono text-lg"
            />
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setManualOpen(false)}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={manualCode.trim().length === 0}>
                Vérifier le code
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </StationChrome>
  );
}

function ReadyScreen({
  checking,
  hint,
  layoutSuspect,
  onManual,
}: {
  checking: boolean;
  hint: string | null;
  layoutSuspect: StationLayout | true | null;
  onManual: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-8 p-6 text-center">
      <div className="relative">
        <span
          aria-hidden="true"
          className="bg-primary/10 absolute inset-0 -m-8 rounded-full"
        />
        <ScanLine
          className={
            checking
              ? "text-primary relative size-20 animate-pulse"
              : "text-primary relative size-20"
          }
          strokeWidth={1.4}
          aria-hidden="true"
        />
      </div>
      <div>
        <p className="font-display text-3xl font-bold tracking-tight">
          {checking ? "Vérification…" : "Prêt à scanner"}
        </p>
        <p className="text-muted-foreground mt-2.5 text-base">
          Présentez le QR de l&apos;invité au lecteur.
        </p>
      </div>

      {hint ? (
        <p className="text-warning flex items-center gap-2 text-[0.9375rem]">
          <TriangleAlert className="size-4" aria-hidden="true" />
          {hint}
        </p>
      ) : null}

      {layoutSuspect ? (
        <p className="text-warning mx-auto flex max-w-lg items-start gap-2.5 rounded-xl bg-white/[0.06] px-4 py-3 text-left text-[0.875rem]">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            {layoutSuspect === true
              ? "Le lecteur et le clavier de ce poste n'ont pas la même disposition."
              : `Ce poste utilise un clavier ${LAYOUT_LABEL[layoutSuspect]} et le lecteur émet en US.`}{" "}
            Les codes sont corrigés automatiquement, mais réglez le lecteur (ou
            le clavier) pour que les deux concordent — une lecture abîmée ne
            pourra pas toujours être rattrapée.
          </span>
        </p>
      ) : null}

      <Button
        variant="ghost"
        className="text-muted-foreground hover:text-foreground h-12"
        onClick={onManual}
      >
        <Keyboard className="size-4" />
        Le QR ne scanne pas ?
      </Button>
    </div>
  );
}

function VerdictScreen({
  verdict,
  raw,
  recorded,
  onDismiss,
  onBlockedDecision,
}: {
  verdict: Verdict;
  raw: string;
  recorded: boolean;
  onDismiss: () => void;
  onBlockedDecision: (
    result: "allow" | "deny",
    reason?: string,
  ) => Promise<void>;
}) {
  const terms = useTerms();
  const [deciding, setDeciding] = useState(false);
  const allow = verdict.result === "allow";
  const blocked = verdict.kind === "guest" ? verdict.blocked : null;
  // The gardien is asked to decide only when the pass itself would have let
  // the visitor through. A blocked DENY was already recorded under its own
  // reason (see `settle`), so its panel is a warning, not a prompt.
  const awaitingDecision = blocked !== null && allow;
  const resident = verdict.kind === "resident" ? verdict.resident : null;
  const invite = verdict.invite;

  const name = resident?.name ?? invite?.guestName ?? null;
  const unit = resident?.unitLabel ?? invite?.unitLabel ?? null;

  // Only when the payload was not found AND it carries the fingerprints of a
  // layout mismatch. Silent corruption is the whole hazard here: the server
  // says "not found" identically for a code that does not exist and for a
  // valid one the scanner garbled.
  const layoutWarning =
    !allow && verdict.reason === "not_found" && looksLayoutMangled(raw);

  return (
    // A full-bleed field of colour is the message; the words are the detail
    // the gardien reads only if the colour was not enough.
    <div
      className={
        allow
          ? "flex flex-1 flex-col items-center justify-center gap-6 bg-[var(--success-700)] p-8 text-center text-white"
          : "flex flex-1 flex-col items-center justify-center gap-6 bg-[var(--danger-700)] p-8 text-center text-white"
      }
      role="status"
      aria-live="assertive"
    >
      <span className="flex size-24 items-center justify-center rounded-full bg-white/20">
        {allow ? (
          <Check className="size-12" strokeWidth={3} aria-hidden="true" />
        ) : (
          <X className="size-12" strokeWidth={3} aria-hidden="true" />
        )}
      </span>

      <div>
        <StationEyebrow className="!text-white/80">
          {allow
            ? verdict.kind === "resident"
              ? terms.residentLabel
              : "Invité"
            : "Accès refusé"}
        </StationEyebrow>
        {/* clamp so a long name still fits on one screen at desk distance */}
        <p className="font-display mt-2 text-[clamp(2rem,6vw,3.5rem)] leading-[1.05] font-bold tracking-tight text-balance">
          {allow ? (name ?? "Accès autorisé") : denyReasonLabel(verdict.reason)}
        </p>
        {allow ? (
          <p className="mt-3 text-xl text-white/90">{unit ?? "—"}</p>
        ) : (
          <p className="mx-auto mt-3 max-w-xl text-lg text-white/90">
            {denyReasonDetail(verdict.reason, terms)}
          </p>
        )}
        {verdict.kind === "guest" ? (
          <div className="mt-3 flex items-center justify-center gap-2 empty:mt-0">
            <VisitorBadges
              visitor={{
                vip: verdict.vip,
                blocked: blocked !== null,
                blockedReason: blocked?.reason ?? null,
              }}
            />
          </div>
        ) : null}
      </div>

      {allow && invite ? (
        <p className="text-base text-white/80">
          {validityLabel(invite, invite.timeZone, terms)}
        </p>
      ) : null}
      {allow && verdict.kind === "resident" ? (
        <p className="text-base text-white/80">
          {terms.residentLabel} · accès permanent
        </p>
      ) : null}

      {/* A deny that names someone still has to say who, so the gardien can
          tell the person in front of them what the problem is. For an
          `off_schedule` deny it also says WHEN the pass does work — that is the
          only question the guest is going to ask, and the gardien should not
          have to phone the syndic to answer it. */}
      {!allow && invite ? (
        <div className="rounded-xl bg-white/12 px-5 py-3 text-base">
          <span className="font-semibold">{invite.guestName}</span>
          <span className="text-white/70"> · {invite.unitLabel}</span>
          {verdict.reason === "off_schedule" ? (
            <span className="mt-1.5 block text-white/70">
              Valable : {validityLabel(invite, invite.timeZone, terms)}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* On an allow, nothing has been logged for this scan yet — see
          `settle` — and one of these two buttons is what puts a fact in the
          journal. On a deny the entry is already written and this is only the
          note the gardien needs to explain the refusal.

          Colour: `--danger-700` is the station's own deny field, so the panel
          reads as danger against the green allow screen; on the deny screen
          that field is ALREADY `--danger-700`, so there it lifts off the
          background with the same white wash every other panel here uses. */}
      {blocked ? (
        <div
          className={
            allow
              ? "mx-auto w-full max-w-xl rounded-xl border border-white/25 bg-[var(--danger-700)] p-5 text-left"
              : "mx-auto w-full max-w-xl rounded-xl border border-white/25 bg-white/12 p-5 text-left"
          }
        >
          <p className="flex items-center gap-2 text-base font-semibold text-white">
            <TriangleAlert className="size-5 shrink-0" aria-hidden="true" />
            Liste noire
          </p>
          <p className="mt-1.5 text-[0.9375rem] text-white/90">
            {blocked.reason ?? `Bloqué par ${terms.theAdmin}.`}
          </p>
          {awaitingDecision ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                variant="destructive"
                className="h-14 min-w-56 text-base"
                disabled={deciding}
                onClick={() => {
                  setDeciding(true);
                  void onBlockedDecision("deny", "blocked").finally(() =>
                    setDeciding(false),
                  );
                }}
              >
                Refuser
              </Button>
              <Button
                variant="secondary"
                className="h-14 min-w-56 text-base"
                disabled={deciding}
                onClick={() => {
                  setDeciding(true);
                  void onBlockedDecision("allow").finally(() =>
                    setDeciding(false),
                  );
                }}
              >
                Admettre malgré tout
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {layoutWarning ? (
        <p className="mx-auto flex max-w-xl items-start gap-2.5 rounded-xl bg-white/12 px-5 py-3 text-left text-[0.9375rem]">
          <TriangleAlert
            className="mt-0.5 size-5 shrink-0"
            aria-hidden="true"
          />
          <span>
            Le lecteur est probablement réglé sur une autre disposition de
            clavier ; il doit être en <strong>US / QWERTY</strong>. Code lu :{" "}
            <span className="font-mono break-all">{raw.slice(0, 96)}</span>
          </span>
        </p>
      ) : null}

      {/* Unchanged: only a blocked ALLOW is left unrecorded on purpose, and
          that screen is not a deny — so every deny that reaches here really
          did fail to write, and says so. */}
      {!recorded && !allow && verdict.reason !== "not_found" ? (
        <p className="text-[0.9375rem] text-white/70">
          Ce refus n&apos;a pas pu être enregistré au journal.
        </p>
      ) : null}

      <Button
        variant="secondary"
        className="mt-2 h-14 min-w-56 text-base"
        onClick={onDismiss}
      >
        Suivant
      </Button>
    </div>
  );
}

function HeadcountScreen({
  verdict,
  onConfirm,
  onCancel,
}: {
  verdict: Verdict;
  onConfirm: (actualHeadcount: number) => void;
  onCancel: () => void;
}) {
  const invite = verdict.invite;
  const max = invite?.maxHeadcount ?? null;
  const [value, setValue] = useState(max !== null ? String(max) : "");
  const parsed = value.trim() ? Number.parseInt(value, 10) : NaN;
  const valid = Number.isInteger(parsed) && parsed >= 1;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 bg-[var(--success-700)] p-8 text-center text-white">
      <StationEyebrow className="!text-white/80">
        Invitation de groupe
      </StationEyebrow>
      <p className="font-display text-[clamp(1.75rem,5vw,3rem)] leading-[1.05] font-bold tracking-tight">
        {invite?.guestName ?? "Invité"}
      </p>
      <p className="text-xl text-white/90">
        {invite?.unitLabel ?? "—"}
        {max !== null ? ` · jusqu'à ${max} personnes` : ""}
      </p>

      <form
        className="flex flex-col items-center gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onConfirm(parsed);
        }}
      >
        <label className="text-base text-white/90" htmlFor="station-headcount">
          Combien de personnes entrent ?
        </label>
        <Input
          id="station-headcount"
          inputMode="numeric"
          autoFocus
          value={value}
          onChange={(e) =>
            setValue(e.target.value.replace(/\D/g, "").slice(0, 2))
          }
          className="h-20 w-32 border-white/30 bg-white/15 text-center font-mono !text-4xl text-white placeholder:text-white/50"
        />
        <div className="flex gap-3">
          <Button
            type="button"
            variant="ghost"
            className="h-14 min-w-40 text-base text-white hover:bg-white/15 hover:text-white"
            onClick={onCancel}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="secondary"
            className="h-14 min-w-56 text-base"
            disabled={!valid}
          >
            Enregistrer l&apos;entrée
          </Button>
        </div>
      </form>
    </div>
  );
}
